import React, { useState } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Alert, Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';

import OrderSearchForm, { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';
import Spinner from '~/components/Common/Spinner';

import { useGetAccommodationV2Purchases } from '~/hooks/apis/orders/useGetAccommodationV2Purchases';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import { AccommodationV2Scope } from '~/services/OrdersService';

import { mustFilterOrders } from '~/utils/adminPermission';
import { addQuery, removeQuery } from '~/utils/url';

import AccommodationV2Page from './AccommodationV2Page';

const DEFAULT_SIZE_PER_PAGE = 10;
const SEARCH_BY_CUSTOMER_ID_SIZE_PER_PAGE = 50;

const SCOPE_OPTIONS: Array<{ value: AccommodationV2Scope; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'native', label: 'Native' },
  { value: 'shim', label: 'Legacy shim' },
];

export default function AccommodationV2PageContainer() {
  const { push } = useAppNavigation();
  const location = useLocation();
  const query = useQuery();
  const { user } = useCurrentUser();
  const brand = useSelector((state: App.State) => state.tenant.brand);

  const [filterField, setFilterField] = useState<string | null>(null);
  const [filterValue, setFilterValue] = useState<string | null>(null);
  const [searchNonce, setSearchNonce] = useState(0);
  const [scope, setScope] = useState<AccommodationV2Scope>('all');
  const [additionalFilter, setAdditionalFilter] = useState<MultiFilterState | null>(null);

  const page = parseInt(query.get('page')) || 1;
  const customerId = query.get('customer_id');
  const perPage = customerId ? SEARCH_BY_CUSTOMER_ID_SIZE_PER_PAGE : DEFAULT_SIZE_PER_PAGE;

  const isRestricted = mustFilterOrders(user);
  const hasFilter = !!filterField && !!filterValue;

  const { data, isLoading, isError, refetch } = useGetAccommodationV2Purchases(
    { brand, page, perPage, scope, customerId, filterField, filterValue, additionalFilter, searchNonce },
    !!user && (!isRestricted || hasFilter),
  );

  const searchQuery = (field: string, value: string) => {
    setFilterField(field);
    setFilterValue(value);
    setSearchNonce((nonce) => nonce + 1);
    push(removeQuery(location, 'customer_id', 'page'));
  };

  const purchases = isRestricted && !hasFilter ? { result: [], total: 0 } : data;

  return (
    <>
      <OrderSearchForm
        searchQuery={searchQuery}
        setAdditionalParams={setAdditionalFilter}
        bookingIdType="bedbank_number"
        bookingNumberPlaceholder="Enter accommodation booking ID"
        e2eSuffix="-accommodation-v2"
        showOrderNumberFilter
      />
      <Box mb={2}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_, value: AccommodationV2Scope | null) => {
            if (value) {
              setScope(value);
              push(removeQuery(location, 'page'));
            }
          }}
          aria-label="Accommodation booking type"
        >
          {SCOPE_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {isLoading && !purchases && <Spinner size={48} />}
      {isError && (
        <Alert
          severity="error"
          action={
            <Button variant="text" color="primary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Unable to load orders. Please retry your search.
        </Alert>
      )}
      {purchases && (
        <AccommodationV2Page
          purchases={purchases.result}
          total={purchases.total}
          page={page}
          sizePerPage={perPage}
          onPageChange={(nextPage) => push(addQuery(location, { page: nextPage }))}
        />
      )}
    </>
  );
}
